import type { ColumnDef, RowData } from '@tanstack/react-table'

/*
 * Column meta lets column definitions carry styling hints
 * without consumers touching the primitives.
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string
    cellClassName?: string
    align?: 'start' | 'end'
  }
}

/*
 * TanStack declares its own columns option as ColumnDef<TData, any>[]
 * because ColumnDef is invariant in TValue: header/footer templates
 * consume HeaderContext<TData, TValue>. Using unknown here would reject
 * every typed accessor (columnHelper.accessor returning number, Date, …).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableColumnDef<TData> = ColumnDef<TData, any>

export interface DataTableSort {
  columnId: string
  direction: 'ascending' | 'descending'
}

export interface DataTableQuery {
  pageIndex: number
  pageSize: number
  sorting: DataTableSort | null
  search: string
}

export interface DataTableClientPagination {
  mode: 'client'
  initialPageSize?: number
  pageSizeOptions?: readonly number[]
}

export interface DataTableServerPagination {
  mode: 'server'
  rowCount: number
  query: DataTableQuery
  onQueryChange: (query: DataTableQuery) => void
  pageSizeOptions?: readonly number[]
}

export type DataTablePaginationConfig =
  | DataTableClientPagination
  | DataTableServerPagination

export interface DataTableActionColumnView<TData> {
  mode: 'view'
  onAction: (row: TData) => void
  getActionLabel: (row: TData) => string
}

export interface DataTableActionColumnSelect<TData> {
  mode: 'select'
  onAction: (row: TData) => void
  getActionLabel: (row: TData) => string

  /**
   * Marks the currently chosen row so the action button can
   * render its selected variant.
   */
  isRowSelected?: (row: TData) => boolean
}

export interface DataTableActionColumnCheckbox {
  mode: 'checkbox'

  /**
   * Complete selection across all pages. The table only adds or
   * removes ids for rows it currently renders; ids selected on
   * other pages survive select-all and deselect-all.
   */
  selectedRowIds: readonly string[]
  onSelectionChange: (ids: string[]) => void
}

export type DataTableActionColumn<TData> =
  | DataTableActionColumnView<TData>
  | DataTableActionColumnSelect<TData>
  | DataTableActionColumnCheckbox

export const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100]

export const DEFAULT_PAGE_SIZE = 25
